import React, {useEffect, useRef, useState} from 'react'
import {isMacLike, useAiCoach} from '../core/AiCoachContext'

const WRAP: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
}

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 999,
  padding: '8px 14px 8px 36px',
  fontSize: 13,
  color: 'var(--text-primary, inherit)',
  outline: 'none',
  height: 38,
  cursor: 'text',
  transition: 'border-color 0.15s, box-shadow 0.15s',
}

const ICON_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 14,
  color: 'rgba(232, 64, 64, 0.95)',
  pointerEvents: 'none',
}

const KBD_HINT_STYLE: React.CSSProperties = {
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  border: '1px solid var(--card-border)',
  borderRadius: 6,
  padding: '2px 6px',
  background: 'transparent',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
}

const BUTTON_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 999,
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-primary, inherit)',
  cursor: 'pointer',
  height: 38,
  whiteSpace: 'nowrap',
}

type Props = {
  /**
   * Compact = icon-only chip used on small screens. Default = full
   * pill input with a placeholder + ⌘K hint, used on lg+ headers.
   */
  variant?: 'pill' | 'chip'
}

/**
 * Header-mounted entry point for the AI Coach. Looks like a search box
 * but submitting the input pops the drawer open and seeds the first
 * prompt automatically.
 */
export const AiCoachLauncher: React.FC<Props> = ({variant = 'pill'}) => {
  const {openCoach} = useAiCoach()
  const [text, setText] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [hint, setHint] = useState<string>('⌘K')

  useEffect(() => {
    setHint(isMacLike() ? '⌘K' : 'Ctrl K')
  }, [])

  const submit = () => {
    const seed = text.trim()
    openCoach(seed.length > 0 ? seed : undefined)
    setText('')
    inputRef.current?.blur()
  }

  if (variant === 'chip') {
    return (
      <button
        type='button'
        title={`Ask the AI Coach (${hint})`}
        aria-label='Ask the AI Coach'
        onClick={() => openCoach()}
        style={BUTTON_STYLE}
      >
        <i className='bi bi-stars' style={{color: 'rgba(232, 64, 64, 0.95)', fontSize: 14}} />
        <span>Ask AI</span>
        <span
          style={{
            fontSize: 10,
            border: '1px solid var(--card-border)',
            borderRadius: 6,
            padding: '1px 5px',
            color: 'var(--text-muted)',
          }}
        >
          {hint}
        </span>
      </button>
    )
  }

  return (
    <div style={{...WRAP, width: 320, maxWidth: '100%'}}>
      <i className='bi bi-stars' style={ICON_STYLE} />
      <input
        ref={inputRef}
        type='text'
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          } else if (e.key === 'Escape') {
            setText('')
            inputRef.current?.blur()
          }
        }}
        onClick={() => {
          // Pure click (no typed text yet) → open the drawer immediately
          // so this also serves as a click-to-launch chip. Once the user
          // types, Enter takes over.
          if (text.length === 0) openCoach()
        }}
        placeholder='Ask your AI Coach…'
        aria-label='Ask your AI Coach'
        style={{
          ...INPUT_STYLE,
          borderColor: focused ? 'rgba(232, 64, 64, 0.55)' : 'var(--card-border)',
          boxShadow: focused ? '0 0 0 2px rgba(232, 64, 64, 0.10)' : 'none',
        }}
      />
      {!focused && text.length === 0 && <span style={KBD_HINT_STYLE}>{hint}</span>}
    </div>
  )
}

export default AiCoachLauncher
